import { createContext, useContext, useState } from "react";
import { MRT_PaginationState, MRT_SortingState } from "material-react-table";
import { useDelayedSearch } from "src/utils/useDelayedSearch";
import { useGetAllDocuments, useUploadDocument } from "src/api/client-api/documentation";
import type { DocumentListContextType, UploadDocumentMutationPayload } from "src/api/client-api/documentation/types";

const DocumentListContext = createContext<DocumentListContextType | null>(null);

type DocumentListProviderProps = {
    children: ReactNode;
};

export const DocumentListProvider = ({ children }: DocumentListProviderProps) => {
    const [search] = useState("");
    const PAGE_SIZE = 10;
    const ALLOWED_MIME_TYPES = [
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        "application/vnd.ms-powerpoint",
        "text/plain",
        "application/rtf",
        "text/csv",
    ];

    const [page, setPage] = useState<MRT_PaginationState>({
        pageIndex: 0,
        pageSize: PAGE_SIZE,
    });

    const [sorting, setSorting] = useState<MRT_SortingState>([
        {
            id: "createdAt",
            desc: true,
        },
    ]);

    const delayedSearchQuery = useDelayedSearch();

    const { data, isLoading } = useGetAllDocuments({
        page,
        sorting,
        search: delayedSearchQuery.delayedSearch,
        mimeType: ALLOWED_MIME_TYPES,
    });

    const uploadMutation = useUploadDocument();

    const handleUpload = async (data: UploadDocumentMutationPayload) => {
        await uploadMutation.query.mutateAsync(data);
    };

    const value: DocumentListContextType = {
        documents: data?.list ?? [],
        isLoading,
        isUploading: uploadMutation.uploading,
        delayedSearchQuery,
        totalItems: data?.totalItems,
        page,
        setPage,
        search,
        // setSearch,
        sorting,
        setSorting,
        uploadDocument: handleUpload,
    };

    return <DocumentListContext.Provider value={value}>{children}</DocumentListContext.Provider>;
};

export const useDocumentListContext = () => {
    const context = useContext(DocumentListContext);
    if (!context) {
        throw new Error("useDocumentListContext must be used within a DocumentListProvider");
    }
    return context;
};
